import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { useData } from '../contexts/DataContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlus, FiCamera, FiTrash2, FiAlertCircle, FiCalendar, FiImage } = FiIcons;

const ImageLog = () => {
  const { data, addImageUpload, deleteEntry } = useData();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    imageUrl: '',
    category: 'growth',
    notes: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    addImageUpload(formData);
    setFormData({
      imageUrl: '',
      category: 'growth',
      notes: ''
    });
    setShowForm(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="p-4 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Image Log</h1>
          <p className="text-gray-600 mt-1">Document your farm with photos</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center space-x-2 bg-pink-500 text-white px-4 py-2 rounded-lg hover:bg-pink-600 transition-colors"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4" />
          <span>Add Image</span>
        </button>
      </div>

      {/* Educational Tip */}
      <div className="bg-pink-50 border-l-4 border-pink-500 p-3 rounded">
        <div className="flex">
          <div className="flex-shrink-0">
            <SafeIcon icon={FiAlertCircle} className="h-5 w-5 text-pink-500" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-pink-800">Photo Documentation Guide</h3>
            <div className="mt-1 text-sm text-pink-700">
              Document your tilapia farm progress with photos. Take clear, well-lit images and include size references 
              for growth photos. Regular documentation helps track progress and identify issues early.
            </div>
          </div>
        </div>
      </div>

      {/* Photo Documentation Guide */}
      <div className="bg-pink-50 p-4 rounded-lg">
        <h3 className="text-lg font-semibold text-pink-800 mb-2">Photo Documentation Guide</h3>
        <div className="space-y-2 text-pink-700">
          <p className="flex items-center">
            <SafeIcon icon={FiCamera} className="w-4 h-4 mr-2" />
            Take clear, well-lit photos
          </p>
          <p className="flex items-center">
            <SafeIcon icon={FiImage} className="w-4 h-4 mr-2" />
            Include size reference in growth photos
          </p>
          <p className="flex items-center">
            <SafeIcon icon={FiAlertCircle} className="w-4 h-4 mr-2" />
            Document any unusual conditions
          </p>
          <p className="flex items-center">
            <SafeIcon icon={FiCalendar} className="w-4 h-4 mr-2" />
            Take photos at consistent intervals
          </p>
        </div>
      </div>

      {/* Add Image Form */}
      {showForm && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Add Image</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Image URL
              </label>
              <input
                type="url"
                name="imageUrl"
                value={formData.imageUrl}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                placeholder="https://example.com/image.jpg"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
              >
                <option value="growth">Growth Documentation</option>
                <option value="tank">Tank Condition</option>
                <option value="feeding">Feeding Activity</option>
                <option value="maintenance">Maintenance Work</option>
                <option value="health">Fish Health</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                placeholder="Description of the image..."
              />
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                className="flex-1 bg-pink-500 text-white py-2 px-4 rounded-lg hover:bg-pink-600 transition-colors"
              >
                Save Image
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="flex-1 bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Image Gallery */}
      <div className="space-y-4">
        {data.imageUploads.length > 0 ? (
          [...data.imageUploads].reverse().map((image) => (
            <motion.div
              key={image.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-200"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
                    <SafeIcon icon={FiCamera} className="w-5 h-5 text-pink-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {format(new Date(image.date), 'MMM dd, yyyy')}
                    </h3>
                    <p className="text-sm text-gray-500 capitalize">{image.category}</p>
                  </div>
                </div>
                <button
                  onClick={() => deleteEntry('imageUploads', image.id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                </button>
              </div>

              <div className="mb-4">
                <img
                  src={image.imageUrl}
                  alt={image.notes || 'Farm documentation'}
                  className="w-full h-48 object-cover rounded-lg"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>

              {image.notes && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-700">{image.notes}</p>
                </div>
              )}
            </motion.div>
          ))
        ) : (
          <div className="text-center py-12">
            <SafeIcon icon={FiCamera} className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No images yet</h3>
            <p className="text-gray-500 mb-6">Start documenting your farm by adding your first image</p>
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center space-x-2 bg-pink-500 text-white px-6 py-3 rounded-lg hover:bg-pink-600 transition-colors"
            >
              <SafeIcon icon={FiPlus} className="w-5 h-5" />
              <span>Add First Image</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageLog;